import type { Guest, Side } from '../../types/board';

const SIDES: { id: Side; label: string }[] = [
  { id: 'bride', label: "Bride's side" },
  { id: 'groom', label: "Groom's side" },
  { id: 'both', label: 'Both' },
];

function Tile({
  label,
  data,
  className = '',
}: {
  label: string;
  data: { count: number; plusOnes: number };
  className?: string;
}) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-muted mb-1">{label}</div>
      <div className={`font-serif text-[22px] sm:text-[27px] tabular-nums ${className}`}>
        {data.count}
        {data.plusOnes > 0 && <span className="text-muted text-[16px] sm:text-[19px]">(+{data.plusOnes})</span>}
      </div>
    </div>
  );
}

export default function Stats({
  guests,
  activeSide,
  onToggleSide,
  search,
  onSearchChange,
}: {
  guests: Guest[];
  activeSide: Side | null;
  onToggleSide: (side: Side) => void;
  search: string;
  onSearchChange: (v: string) => void;
}) {
  // count = guests in bucket, plusOnes = how many of them bring a +1 (shown as "count(+plusOnes)")
  const count = (list: Guest[]) => ({ count: list.length, plusOnes: list.filter((g) => g.plusOne).length });
  const invited = count(guests);
  const confirmed = count(guests.filter((g) => g.rsvp === 'confirmed'));
  const declined = count(guests.filter((g) => g.rsvp === 'declined'));
  const pending = count(guests.filter((g) => g.rsvp === 'pending'));
  const arrived = count(guests.filter((g) => g.checkedIn));
  const cPct = invited.count ? (confirmed.count / invited.count) * 100 : 0;
  const dPct = invited.count ? (declined.count / invited.count) * 100 : 0;

  return (
    <div className="mb-8 border border-line bg-card px-4 sm:px-10 py-7 sm:py-9">
      <div className="font-display italic text-5xl leading-none mb-5">Guest List</div>

      <div className="grid grid-cols-3 sm:grid-cols-5 border-t border-line pt-7 text-center gap-y-5 sm:gap-y-0 sm:divide-x divide-line">
        <Tile label="Invited" data={invited} />
        <Tile label="Confirmed" data={confirmed} className="text-sage" />
        <Tile label="Declined" data={declined} className="text-terra" />
        <Tile label="Pending" data={pending} />
        <Tile label="Checked in" data={arrived} className="text-sage" />
      </div>

      <div className="mt-6">
        <div className="h-2 rounded-full bg-[oklch(92%_0.012_60)] overflow-hidden flex">
          <div className="h-full bg-sage" style={{ width: `${cPct}%` }} />
          <div className="h-full bg-[oklch(70%_0.09_20)]" style={{ width: `${dPct}%` }} />
        </div>
        <div className="mt-1.5 text-xs text-muted text-right">
          {confirmed.count} of {invited.count} guests confirmed — (+N) is guests bringing a plus-one
        </div>
      </div>

      <div className="mt-7">
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onDrop={(e) => e.preventDefault()}
          placeholder="Search guests by name or notes…"
          className="w-full max-w-[420px] border border-line bg-cream px-3.5 py-2.5 text-sm outline-none focus:border-terra"
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2.5">
        {SIDES.map((s) => {
          const active = activeSide === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onToggleSide(s.id)}
              className="rounded-full px-3.5 py-1.5 text-sm font-semibold"
              style={{
                border: `1.5px solid ${active ? 'oklch(50% 0.1 20)' : 'oklch(88% 0.015 60)'}`,
                background: active ? 'oklch(50% 0.1 20)' : 'transparent',
                color: active ? 'oklch(99% 0.006 75)' : 'oklch(24% 0.02 50)',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
